<template>
  <div class="relative flex min-w-0 border-b px-5 shrink-0">
    <div
      class="sticky left-0 z-10 flex h-[5.3rem] w-80 flex-wrap items-center justify-between space-x-8 bg-white py-5"
    >
      <lf-checkbox
        name="offer-checkbox"
        data-cy="offer-checkbox"
        class="w-max"
        value=""
        v-model="isAllSelected"
      >
        {{ t("COMMON.OFFER", 2) }}
      </lf-checkbox>

      <div class="flex pr-3">
        <loader :is-loading="isBulkUpdatingSharedState" />
        <toggle-offer-shared
          v-if="showSharedIcon"
          v-tooltip="tootipText"
          v-model="hasOfferNotShared"
          class="mt-1"
          data-cy="offer-shared"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts" generic="T extends IOffer">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useDeals } from "@/hooks/deals";
import { useBulkToggleOffersSharedMutation } from "@/hooks/queries/applications";

import Loader from "@/components/ui/Loader.vue";
import ToggleOfferShared from "@/components/ui/ToggleOfferShared.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { IOffer } from "@/models/funders";

const { selectedOffers = [], offers } = defineProps<{
  selectedOffers?: IOffer[];
  offers: IOffer[];
}>();

const emit = defineEmits<{
  bulkSelectionChange: [boolean];
}>();

const { t } = useI18n();
const { activeDeal } = useDeals();
const {
  mutateAsync: bulkToggleSharedState,
  isPending: isBulkUpdatingSharedState,
} = useBulkToggleOffersSharedMutation();

const selectedOffersIds = computed(() => selectedOffers.map(({ id }) => id));

const selectedOffersOnTable = computed(() =>
  offers.filter((offer) => selectedOffersIds.value.includes(offer.id))
);

const isAllSelected = computed({
  get: () => selectedOffersOnTable.value.length === offers.length,
  set: (value) => {
    emit("bulkSelectionChange", value);
  },
});

const showSharedIcon = computed(() => {
  return (
    !!selectedOffers.length &&
    !!offers.length &&
    offers.find((offer) => selectedOffersIds.value.includes(offer.id))
  );
});

const hasOfferNotShared = computed({
  get: () => !!selectedOffersOnTable.value.find(({ is_shared }) => !is_shared),
  set: () => updateSharedOffers(),
});

const tootipText = computed(
  () =>
    `${hasOfferNotShared.value ? t("COMMON.SHOW") : t("COMMON.HIDE")} ${t("COMMON.ALL")}`
);

const updateSharedOffers = async () => {
  await bulkToggleSharedState({
    dealId: activeDeal.value.id,
    data: {
      is_shared: hasOfferNotShared.value,
      offer_ids: selectedOffersOnTable.value.map(({ id }) => id),
    },
  });
  emit("bulkSelectionChange", false);
};
</script>
